import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AUDIT_PERSON_KINDS, type AuditPersonKind } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { AuditEntries, downloadBlob } from '../../components/audit/AuditEntries';

const BACK: Record<AuditPersonKind, { to: string; label: string }> = {
  tenant: { to: '/admin/tenants', label: 'Tenants' },
  staff: { to: '/admin/staff', label: 'Staff' },
  contractor: { to: '/admin/contractors', label: 'Contractors' },
};

/**
 * 0.26.0 — one person's activity (owner-only): what they did, what was done about them, their
 * sign-ins, and (tenants) the online-payment steps on their lease. Newest first, "Load more" pages.
 */
export function AdminPersonActivity() {
  const { kind = '', id = '' } = useParams();
  const valid = (AUDIT_PERSON_KINDS as readonly string[]).includes(kind) && id !== '';
  const k = kind as AuditPersonKind;
  const query = useInfiniteQuery({
    queryKey: ['admin', 'audit', 'person', kind, id],
    queryFn: ({ pageParam }) => api.audit.person(k, id, { cursor: pageParam, limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: valid,
  });
  const [exportErr, setExportErr] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  if (!valid) return <ErrorState message="Unknown person." />;
  if (query.isLoading) return <Loading label="Loading activity…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />;

  const person = query.data.pages[0]!.person;
  const items = query.data.pages.flatMap((p) => p.items);

  async function exportXlsx() {
    setExportErr(null);
    setExporting(true);
    try {
      downloadBlob(await api.audit.personExportXlsx(k, id), `activity-${k}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      setExportErr(errorMessage(e));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <p>
        <Link to={BACK[k].to}>← {BACK[k].label}</Link>
      </p>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h1>Activity: {person.name}</h1>
        <button type="button" className="secondary" disabled={exporting} onClick={() => void exportXlsx()}>
          {exporting ? 'Exporting…' : 'Export .xlsx'}
        </button>
      </div>
      <p className="subtitle">
        Everything {person.name} did, everything done about them, their sign-ins
        {k === 'tenant' ? ', and the online payment steps on their lease' : ''} — newest first. Details are redacted:
        field values, secrets and full addresses are never shown.
      </p>
      {exportErr && <StatusMessage tone="err">{exportErr}</StatusMessage>}
      <AuditEntries items={items} showRelation />
      {query.hasNextPage && (
        <p>
          <button type="button" className="secondary" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </p>
      )}
    </>
  );
}
